class HeroCarousel {
    constructor() {
        this.carousel = null;
        this.bullets = null;
        this.prevBtn = null;
        this.nextBtn = null;
        this.currentSlide = 0;
        this.totalSlides = 0;
        this.isTransitioning = false;
        
        // Touch/Swipe variables
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.minSwipeDistance = 50;
        
        this.init();
    }
    
    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.initializeCarousel());
        } else {
            this.initializeCarousel();
        }
    }
    
    initializeCarousel() {
        this.carousel = document.getElementById('heroCarousel');
        this.bullets = document.querySelectorAll('.carousel-bullet');
        this.prevBtn = document.querySelector('.carousel-btn-prev');
        this.nextBtn = document.querySelector('.carousel-btn-next');
        this.totalSlides = this.bullets.length;
        
        if (!this.carousel || this.totalSlides === 0) {
            return;
        }
        
        this.bindEvents();
        this.updateBulletStyles();
        this.updateButtonStates();
        
        // Store globally for debugging
        window.heroCarousel = this;
        
        // Auto-play (optional - uncomment if desired)
        // this.startAutoPlay();
    }
    
    bindEvents() {
        
        // Button events
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.prevSlide();
            });
        }
        
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.nextSlide();
            });
        }
        
        // Bullet events
        this.bullets.forEach((bullet, index) => {
            bullet.addEventListener('click', (e) => {
                e.preventDefault();
                this.goToSlide(index);
            });
        });
        
        // Touch events for mobile
        if (this.carousel) {
            this.carousel.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
            this.carousel.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
        }
        
        // Keyboard navigation
        document.addEventListener('keydown', (e) => this.handleKeydown(e));
        
        // Pause auto-play on hover (if auto-play is enabled)
        if (this.carousel) {
            this.carousel.addEventListener('mouseenter', () => this.pauseAutoPlay());
            this.carousel.addEventListener('mouseleave', () => this.startAutoPlay());
        }
    }
    
    handleTouchStart(e) {
        this.touchStartX = e.changedTouches[0].screenX;
    }
    
    handleTouchEnd(e) {
        this.touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe();
    }
    
    handleSwipe() {
        const swipeDistance = this.touchStartX - this.touchEndX;
        
        if (Math.abs(swipeDistance) > this.minSwipeDistance) {
            if (swipeDistance > 0) {
                // Swipe left - next slide
                this.nextSlide();
            } else {
                // Swipe right - previous slide
                this.prevSlide();
            }
        }
    }
    
    handleKeydown(e) {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.prevSlide();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.nextSlide();
        }
    }
    
    prevSlide() {
        if (this.isTransitioning) return;
        
        this.currentSlide = this.currentSlide === 0 ? this.totalSlides - 1 : this.currentSlide - 1;
        this.updateSlide();
    }
    
    nextSlide() {
        if (this.isTransitioning) return;
        
        this.currentSlide = this.currentSlide === this.totalSlides - 1 ? 0 : this.currentSlide + 1;
        this.updateSlide();
    }
    
    goToSlide(index) {
        if (this.isTransitioning || index === this.currentSlide) return;
        
        this.currentSlide = index;
        this.updateSlide();
    }
    
    updateSlide() {
        if (!this.carousel) return;
        
        this.isTransitioning = true;
        
        // Update transform
        const translateX = -this.currentSlide * 100;
        this.carousel.style.transform = `translateX(${translateX}%)`;
        
        // Update bullet styles
        this.updateBulletStyles();
        
        // Update button states
        this.updateButtonStates();
        
        // Reset transition flag after animation completes
        setTimeout(() => {
            this.isTransitioning = false;
        }, 600);
    }
    
    updateBulletStyles() {
        
        this.bullets.forEach((bullet, index) => {
            // Remove all classes first
            bullet.classList.remove('active', 'dashed');
            
            if (index === this.currentSlide) {
                bullet.classList.add('active');
            } else {
                // Add dashed style to adjacent bullets
                bullet.classList.add('dashed');
            }
        });
    }
    
    updateButtonStates() {
        // Enable/disable buttons based on current slide
        // Note: For infinite carousel, we should not disable buttons 
        /*
        this.prevBtn.disabled = this.currentSlide === 0;
        this.nextBtn.disabled = this.currentSlide === this.totalSlides - 1;
        */
    }
    
    startAutoPlay() {
        if (this.autoPlayInterval) return;
        
        this.autoPlayInterval = setInterval(() => {
            this.nextSlide();
        }, 5000); // Change slide every 5 seconds
    }
    
    pauseAutoPlay() {
        if (this.autoPlayInterval) {
            clearInterval(this.autoPlayInterval);
            this.autoPlayInterval = null;
        }
    }
    
    destroy() {
        this.pauseAutoPlay();
        
        // Remove event listeners
        this.prevBtn?.removeEventListener('click', () => this.prevSlide());
        this.nextBtn?.removeEventListener('click', () => this.nextSlide());
        
        this.bullets.forEach((bullet, index) => {
            bullet.removeEventListener('click', () => this.goToSlide(index));
        });
        
        this.carousel?.removeEventListener('touchstart', (e) => this.handleTouchStart(e));
        this.carousel?.removeEventListener('touchend', (e) => this.handleTouchEnd(e));
        document.removeEventListener('keydown', (e) => this.handleKeydown(e));
    }
}

// Initialize carousel immediately
new HeroCarousel();

// Handle page visibility changes to pause/resume auto-play
document.addEventListener('visibilitychange', () => {
    const carousel = window.heroCarousel;
    if (carousel) {
        if (document.hidden) {
            carousel.pauseAutoPlay();
        } else {
            carousel.startAutoPlay();
        }
    }
});
